import { useCallback, useEffect, useState } from 'react';
import { getEventAvailability } from '../../store/ForNeighborsApi';
import { EventAvailability } from '../../types/Site';
import { logError } from '../../utils/logger';
import {
  MOCK_EVENT_AVAILABILITY_ENABLED,
  MOCK_FETCH_DELAY_MS,
  mockAvailabilityForWindow,
} from './mockEventAvailability';

export interface UseEventAvailabilityResult {
  availability: EventAvailability | null;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export function useEventAvailability(
  occurrenceId: string | null,
  // TODO: drop this param once mockEventAvailability.ts is deleted -- the real (non-mock) path
  // below never reads it.
  mockTimeWindow?: { opensAt: string; closesAt: string }
): UseEventAvailabilityResult {
  const [availability, setAvailability] = useState<EventAvailability | null>(null);
  const [loading, setLoading] = useState(occurrenceId !== null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (occurrenceId === null) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    (async () => {
      try {
        if (MOCK_EVENT_AVAILABILITY_ENABLED && mockTimeWindow) {
          await new Promise((resolve) => {
            setTimeout(resolve, MOCK_FETCH_DELAY_MS);
          });
          if (cancelled) return;
          setAvailability(
            mockAvailabilityForWindow(occurrenceId, mockTimeWindow.opensAt, mockTimeWindow.closesAt)
          );
          return;
        }
        const response = await getEventAvailability(occurrenceId);
        if (cancelled) return;
        setAvailability(response.availability);
      } catch (e) {
        if (cancelled) return;
        logError(e as Error, { occurrenceId });
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [occurrenceId, attempt, mockTimeWindow]);

  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  return { availability, loading, error, refetch };
}
